import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import IconButton from '@mui/material/IconButton';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import '../assets/styles/PortfolioPdfDeck.scss';

const publicUrl = process.env.PUBLIC_URL || '';
pdfjs.GlobalWorkerOptions.workerSrc = `${publicUrl}/pdf.worker.min.mjs`;

const PDF_PATH = `${publicUrl}/jungwoo_ryu_portfolio.pdf`;

function PortfolioPdfDeck() {
  const frameRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState(640);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);

  const file = useMemo(() => PDF_PATH, []);

  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    const update = () => {
      const w = el.clientWidth;
      setPageWidth(Math.min(960, Math.max(260, w - 48)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const goPrev = useCallback(() => {
    setPageNumber((p) => Math.max(1, p - 1));
  }, []);

  const goNext = useCallback(() => {
    setPageNumber((p) => (numPages ? Math.min(numPages, p + 1) : p + 1));
  }, [numPages]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  const onDocLoad = useCallback(({ numPages: total }: { numPages: number }) => {
    setNumPages(total);
    setPageNumber(1);
  }, []);

  return (
    <div className="portfolio-deck-section" id="portfolio-deck">
    <section className="portfolio-pdf-deck" aria-label="Portfolio presentation preview">
      <div className="portfolio-pdf-deck__header">
        <h2 className="portfolio-pdf-deck__title">Portfolio slides</h2>
        <p className="portfolio-pdf-deck__subtitle">PDF deck preview — use arrows or keyboard ← →</p>
        <a className="portfolio-pdf-deck__open" href={PDF_PATH} target="_blank" rel="noreferrer">
          Open full PDF
        </a>
      </div>

      <div className="portfolio-pdf-deck__frame">
        <div className="portfolio-pdf-deck__toolbar">
          <IconButton
            onClick={goPrev}
            disabled={pageNumber <= 1}
            aria-label="Previous slide"
            color="inherit"
            size="small"
          >
            <ChevronLeft />
          </IconButton>
          <span className="portfolio-pdf-deck__counter">
            {numPages ? `${pageNumber} / ${numPages}` : '…'}
          </span>
          <IconButton
            onClick={goNext}
            disabled={numPages !== null && pageNumber >= numPages}
            aria-label="Next slide"
            color="inherit"
            size="small"
          >
            <ChevronRight />
          </IconButton>
        </div>

        <div className="portfolio-pdf-deck__stage" ref={frameRef}>
          <Document
            file={file}
            onLoadSuccess={onDocLoad}
            loading={<div className="portfolio-pdf-deck__loading">Loading PDF…</div>}
            error={<div className="portfolio-pdf-deck__error">Could not load the portfolio PDF.</div>}
          >
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </div>

        {numPages ? (
          <div className="portfolio-pdf-deck__dots" role="tablist" aria-label="Slides">
            {Array.from({ length: numPages }, (_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i + 1 === pageNumber}
                aria-label={`Slide ${i + 1}`}
                className={i + 1 === pageNumber ? 'is-active' : ''}
                onClick={() => setPageNumber(i + 1)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
    </div>
  );
}

export default PortfolioPdfDeck;
